import { contentSchema } from "@-/dev.improve.haniverse.database.schemas.content.schema";
import { normalizeLocation, type ImportMetaLocation } from "@-/dev.improve.haniverse.lib.logger";
import mapObject from "@-/com.npmjs.map-obj";
import memoize from "@-/com.npmjs.memoize";
import { createContentTableQueries } from "#create-content-table-queries";
import type { ContentQueries, ContentQueryTable, ContentTableQueries } from "#types";

// Shim so each call returns the precise per-table queries; without it the widened
// `ContentQueryTable` collapses the factory's config params to `never`.
/* eslint-disable typescript/no-unsafe-type-assertion -- intentional signature shim */
const createContentQueriesForTable = createContentTableQueries as <
  TTable extends ContentQueryTable,
>(
  location: ImportMetaLocation,
  table: TTable,
) => ContentTableQueries<TTable>;
/* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */

/**
 * The per-table content query map, bound to the caller's `import.meta.location`
 * so query logs are attributed to the read's origin. Call as
 * `contentQueries(import.meta.location).<table>.<findFirst|findMany>.…`; pass
 * `null` to opt out of logging (tests).
 *
 * Memoized on the location: `import.meta.location` is a fresh object literal per
 * evaluation, so the map is rebuilt once per distinct callsite rather than every
 * render.
 */
export const contentQueries: ContentQueries = memoize(
  (location: ImportMetaLocation) =>
    // `mapObject` yields a uniform value type; the built map is a per-table mapped type.
    /* eslint-disable typescript/no-unsafe-type-assertion -- per-table mapped shape */
    mapObject(contentSchema, (table) => [
      table,
      createContentQueriesForTable(location, table),
    ]) as unknown as ReturnType<ContentQueries>,
  /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */ {
    cacheKey: ([location]) => normalizeLocation(location),
  },
);
